import { History, MoreVert } from '@mui/icons-material';
import {
  Card,
  CardActions,
  CardMedia,
  Drawer,
  IconButton,
  Typography,
} from '@mui/material';
import { isImage, OneMedia } from '@prismamedia/one-components';
import { EditorState } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import { FC, memo, useMemo, useState } from 'react';
import { resizeURL } from 'utils/resizer';
import { ArticleStatus } from '../../../../../../../__generated__/queries-web';
import {
  extractSildeshowBodyMedia,
  getThumbnail,
} from '../../../../../../../utils/media';
import { ResourceType } from '../../../utils/useGetBlockItems';
import { BlockItemFormat } from '../../desktop/BlockItem';
import { Menu } from '../../desktop/BlockItem/Content/Menu';
import { BlockItemType, getAlreadyUsedText, isOneMedia } from '../../utils';
import { DrawerBlock } from '../DrawerBlock';
import { DrawerResource } from '../DrawerResource';
import { useStyles } from '../styles';

interface BlockItemProps {
  item: BlockItemType;
  format: BlockItemFormat;
  isInSection?: boolean;
  onDeleteBlock?: () => void;
  onSetLeadIn?: () => void;
  onEditField?: (field: string, value: string | OneMedia | EditorState) => void;
  onAddArticle?: (
    sectionId: string,
    blockType: string,
    articleData: BlockItemType,
  ) => void;
  sectionsIds?: string[];
  isDesktop?: boolean;
}

export const BlockItemMobile: FC<BlockItemProps> = memo(
  ({
    format: blockItemFormat,
    isDesktop,
    isInSection = false,
    item,
    onAddArticle,
    onDeleteBlock,
    onEditField,
    onSetLeadIn,
    sectionsIds,
  }) => {
    const {
      blockType,
      campaigns,
      content,
      isAutoPromo,
      leadIn,
      media,
      body,
      status,
      surTitle,
      title,
      url,
    } = item;
    const finalUrl = url && !url.startsWith('http') ? `https://${url}` : url;
    const classes = useStyles();
    const usedCampaign = !isInSection && campaigns;
    const [showDrawer, setShowDrawer] = useState<boolean>(false);
    const onSectionClick = (sectionId: string) => {
      if (onAddArticle) {
        onAddArticle(sectionId, item.blockType || '', item);
      }
    };
    let image: string | undefined;

    if (isOneMedia(media)) {
      const { canonical, extension } = media.iframely.meta;
      const imageUrl = isImage(media) ? canonical : getThumbnail(media);
      image = resizeURL(imageUrl, extension && extension.toLowerCase());
    }

    if (blockType === ResourceType.DIAPO && body) {
      const { canonical, extension } = extractSildeshowBodyMedia(body);

      image =
        extension &&
        canonical &&
        resizeURL(canonical, extension.toLocaleLowerCase());
    }

    const ifNoMedia = useMemo(
      () => (media && !media.iframely) || (!media && !body),
      [body, media],
    );

    const displayedTitle = useMemo(
      () =>
        isAutoPromo
          ? finalUrl
          : title
          ? typeof title === 'string'
            ? title
            : stateToHTML(title.getCurrentContent())
          : null,
      [isAutoPromo, finalUrl, title],
    );

    return (
      <div className={classes.wrapper}>
        <Card className={classes.innerWrapper}>
          {image && <CardMedia image={image} className={classes.media} />}
          {ifNoMedia && <div className={classes.media} />}
          <div className={classes.content}>
            <Typography
              variant="body2"
              color="textSecondary"
              component="p"
              className={classes.title}
              dangerouslySetInnerHTML={{ __html: displayedTitle || '' }}
            ></Typography>
            {!isInSection && usedCampaign && (
              <Typography
                data-testid="already-used-caption"
                variant="caption"
                color="textSecondary"
                classes={{ root: classes.alreadyUsed }}
              >
                {getAlreadyUsedText(usedCampaign)}
              </Typography>
            )}
          </div>
          <CardActions>
            {isDesktop ? (
              <Menu
                articlePublicURL={finalUrl || ''}
                blockItemFormat={blockItemFormat}
                onSetLeadIn={onSetLeadIn}
                onDeleteBlock={onDeleteBlock}
              />
            ) : (
              <IconButton
                onClick={() => setShowDrawer((prevDrawer) => !prevDrawer)}
                size="large"
              >
                <MoreVert />
              </IconButton>
            )}
            {!isInSection && status === ArticleStatus.Scheduled && (
              <div className={classes.scheduledIcon} title="Article programmé">
                <History />
              </div>
            )}
          </CardActions>
        </Card>
        <Drawer
          anchor="bottom"
          open={showDrawer}
          onClose={() => setShowDrawer(false)}
        >
          {!isInSection && (
            <DrawerResource
              url={finalUrl}
              sectionsIds={sectionsIds}
              onSectionClick={onSectionClick}
            />
          )}
          {isInSection && (
            <DrawerBlock
              leadIn={leadIn}
              isAutoPromo={isAutoPromo}
              onDeleteBlock={onDeleteBlock}
              onSetLeadIn={onSetLeadIn}
              onEditField={onEditField}
              url={finalUrl}
              title={title}
              surTitle={surTitle}
              content={content}
            />
          )}
        </Drawer>
      </div>
    );
  },
);
